import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { ApplicationPage } from '../features/applications/application-page.tsx';
import { ApplicationsPage } from '../features/applications/applications-page.tsx';
import { AftersalesPage } from '../features/aftersales/aftersales-page.tsx';
import { AgentsPage } from '../features/agents/agents-page.tsx';
import { SessionPage } from '../features/agents/session-page.tsx';
import { TasksPage } from '../features/agents/tasks-page.tsx';
import { AuditPage } from '../features/audit/audit-page.tsx';
import { ArticlePage } from '../features/knowledge/article-page.tsx';
import { KnowledgePage } from '../features/knowledge/knowledge-page.tsx';
import { SkillPage, SkillsPage } from '../features/knowledge/skills-page.tsx';
import { LoginPage } from '../features/auth/login-page.tsx';
import { ActivityPage } from '../features/auth/activity-page.tsx';
import { ForbiddenPage, NotFoundPage } from '../features/auth/forbidden-page.tsx';
import {
  CONFIG_SECTIONS,
  ConfigPage,
  type ConfigSectionSlug,
} from '../features/config/config-page.tsx';
import { CustomerPage } from '../features/customers/customer-page.tsx';
import { CustomersPage } from '../features/customers/customers-page.tsx';
import { LinePage } from '../features/customers/line-page.tsx';
import { DemoPage } from '../features/demo/demo-page.tsx';
import { GuidePage } from '../features/guide/guide-page.tsx';
import { HomePage } from '../features/home/home-page.tsx';
import { TransactionPage } from '../features/transactions/transaction-page.tsx';
import { TransactionsPage } from '../features/transactions/transactions-page.tsx';
import { can, type Permission } from '../lib/rbac.ts';
import { sessionStore } from '../lib/session.ts';
import {
  validateAftersalesSearch,
  validateAgentSessionsSearch,
  validateApplicationSearch,
  validateApplicationsSearch,
  validateAuditSearch,
  validateCustomersSearch,
  validateLineSearch,
  validateLoginSearch,
  validateTransactionsSearch,
} from './search.ts';
import { AppShell } from './shell.tsx';

export interface RouterContext {
  queryClient: QueryClient;
}

/** Guarda de ruta: sin sesión → login; sin permiso → "Sin acceso". El servidor vuelve a validar. */
function requirePermission(permission: Permission) {
  return () => {
    const session = sessionStore.get();
    if (!session) return;
    if (!can(session.user.roles, permission)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- API de TanStack Router
      throw redirect({ to: '/sin-acceso' });
    }
  };
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFoundPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: validateLoginSearch,
  component: LoginPage,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: ({ location }) => {
    if (!sessionStore.get()) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- API de TanStack Router
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
  },
  component: AppShell,
});

const homeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  beforeLoad: requirePermission('dashboard:read'),
  component: HomePage,
});

const forbiddenRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/sin-acceso',
  component: ForbiddenPage,
});

const activityRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/mi-actividad',
  component: ActivityPage,
});

const transactionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/transacciones',
  validateSearch: validateTransactionsSearch,
  beforeLoad: requirePermission('transactions:read'),
  component: TransactionsPage,
});

const transactionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/transacciones/$paymentIntentId',
  beforeLoad: requirePermission('transactions:read'),
  component: TransactionPage,
});

const applicationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/expedientes',
  validateSearch: validateApplicationsSearch,
  beforeLoad: requirePermission('applications:read'),
  component: ApplicationsPage,
});

const applicationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/expedientes/$applicationId',
  validateSearch: validateApplicationSearch,
  beforeLoad: requirePermission('applications:read'),
  component: ApplicationPage,
});

const agentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/agentes',
  validateSearch: validateAgentSessionsSearch,
  beforeLoad: requirePermission('agents:read'),
  component: AgentsPage,
});

const agentSessionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/agentes/sesiones/$sessionId',
  beforeLoad: requirePermission('agents:read'),
  component: SessionPage,
});

const tasksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/tareas',
  beforeLoad: requirePermission('hitl:read'),
  component: TasksPage,
});

const knowledgeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/conocimiento',
  beforeLoad: requirePermission('knowledge:read'),
  component: KnowledgePage,
});

const articleRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/conocimiento/$articleId',
  beforeLoad: requirePermission('knowledge:read'),
  component: ArticlePage,
});

const skillsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/skills',
  beforeLoad: requirePermission('knowledge:read'),
  component: SkillsPage,
});

const skillRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/skills/$code',
  beforeLoad: requirePermission('knowledge:read'),
  component: SkillPage,
});

const aftersalesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/postventa',
  validateSearch: validateAftersalesSearch,
  beforeLoad: requirePermission('disputes:read'),
  component: AftersalesPage,
});

const customersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/clientes',
  validateSearch: validateCustomersSearch,
  beforeLoad: requirePermission('customers:read'),
  component: CustomersPage,
});

const customerRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/clientes/$customerId',
  beforeLoad: requirePermission('customers:read'),
  component: CustomerPage,
});

const lineRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/lineas/$lineId',
  validateSearch: validateLineSearch,
  beforeLoad: requirePermission('customers:read'),
  component: LinePage,
});

const configIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/configuracion',
  beforeLoad: () => {
    requirePermission('config:read')();
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- API de TanStack Router
    throw redirect({ to: '/configuracion/$section', params: { section: 'decision' } });
  },
});

const configRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/configuracion/$section',
  params: {
    parse: (raw: { section: string }): { section: ConfigSectionSlug } => {
      const found = CONFIG_SECTIONS.find((s) => s.slug === raw.section);
      return { section: found ? found.slug : 'decision' };
    },
    stringify: (params: { section: ConfigSectionSlug }) => ({ section: params.section }),
  },
  beforeLoad: requirePermission('config:read'),
  component: ConfigPage,
});

const auditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/auditoria',
  validateSearch: validateAuditSearch,
  beforeLoad: requirePermission('audit:read'),
  component: AuditPage,
});

const demoRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/demo',
  beforeLoad: requirePermission('demo:operate'),
  component: DemoPage,
});

const guideRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/guia',
  component: GuidePage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  appRoute.addChildren([
    homeRoute,
    forbiddenRoute,
    activityRoute,
    transactionsRoute,
    transactionRoute,
    applicationsRoute,
    applicationRoute,
    aftersalesRoute,
    agentsRoute,
    agentSessionRoute,
    tasksRoute,
    knowledgeRoute,
    articleRoute,
    skillsRoute,
    skillRoute,
    customersRoute,
    customerRoute,
    lineRoute,
    configIndexRoute,
    configRoute,
    auditRoute,
    demoRoute,
    guideRoute,
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    scrollRestoration: true,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
